import { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import type {
  UseImageEditorResult,
  WatermarkBlock,
} from '../../hooks/useImageEditor';
import {
  WATERMARK_FONT_SIZE_MIN,
  WATERMARK_FONT_SIZE_ABS_MAX,
} from '../../lib/image-editor/overlayLimits';

interface WatermarkPanelProps {
  editor: UseImageEditorResult;
  /** 由外部指定要展開的區塊（例如從預覽點擊文字跳轉時） */
  expandBlockId?: string | null;
  /** 字級上限（依輸出畫布像素）；未傳時用絕對上限 500 */
  maxFontSize?: number;
}

const FONT_OPTIONS: { value: string; label: string }[] = [
  { value: 'sans-serif', label: '無襯線 (sans-serif)' },
  { value: 'serif', label: '襯線 (serif)' },
  { value: 'monospace', label: '等寬 (monospace)' },
  { value: 'Arial', label: 'Arial' },
  { value: 'Georgia', label: 'Georgia' },
  { value: 'Microsoft JhengHei', label: '微軟正黑體' },
  { value: 'Noto Sans TC', label: '思源黑體' },
];

/**
 * 文字／浮水印設定：多個文字塊，在畫布尺寸之後套用，輸出時繪製於最終畫布上。
 */
export function WatermarkPanel({
  editor,
  expandBlockId,
  maxFontSize = WATERMARK_FONT_SIZE_ABS_MAX,
}: WatermarkPanelProps) {
  const fontMax = Math.max(WATERMARK_FONT_SIZE_MIN, maxFontSize);
  const {
    watermarkItems,
    addWatermarkItem,
    removeWatermarkItem,
    updateWatermarkItem,
  } = editor;

  /** 手風琴：僅一個區塊展開，null 表示全部收合 */
  const [expandedId, setExpandedId] = useState<string | null>(null);
  useEffect(() => {
    if (expandBlockId != null) setExpandedId(expandBlockId);
  }, [expandBlockId]);

  const toggleBlock = (id: string) => {
    setExpandedId(prev => (prev === id ? null : id));
  };

  const isCollapsed = (id: string) => expandedId !== id;

  return (
    <div className="space-y-4">
      {watermarkItems.map((block: WatermarkBlock, index: number) => {
        const blockCollapsed = isCollapsed(block.id);
        return (
          <div
            key={block.id}
            className="rounded-lg border border-gray-200 bg-white dark:border-gray-600 dark:bg-gray-800/50"
          >
            <div className="flex items-center justify-between gap-2 p-3">
              <button
                type="button"
                onClick={() => toggleBlock(block.id)}
                className="flex flex-1 items-center gap-1.5 text-left text-xs font-medium text-gray-600 dark:text-gray-400"
                aria-expanded={!blockCollapsed}
              >
                {blockCollapsed ? (
                  <ChevronDown className="h-4 w-4 shrink-0" aria-hidden />
                ) : (
                  <ChevronUp className="h-4 w-4 shrink-0" aria-hidden />
                )}
                <span className="truncate">
                  {block.text.trim()
                    ? block.text.trim()
                    : `文字塊 ${index + 1}`}
                </span>
              </button>
              <button
                type="button"
                onClick={() => removeWatermarkItem(block.id)}
                disabled={watermarkItems.length <= 1}
                className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-red-600 disabled:opacity-40 dark:hover:bg-gray-700 dark:hover:text-red-400"
                aria-label="刪除此文字塊"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            {!blockCollapsed && (
              <div className="space-y-3 border-t border-gray-200 p-3 dark:border-gray-600">
                <input
                  type="text"
                  value={block.text}
                  onChange={e =>
                    updateWatermarkItem(block.id, { text: e.target.value })
                  }
                  placeholder="輸入文字"
                  className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-500"
                  maxLength={200}
                  aria-label="文字內容"
                />
                <div className="flex flex-wrap items-end gap-3">
                  <div className="min-w-0 flex-1">
                    <label className="mb-0.5 block text-xs text-gray-500 dark:text-gray-400">
                      字型
                    </label>
                    <select
                      value={block.fontFamily}
                      onChange={e =>
                        updateWatermarkItem(block.id, {
                          fontFamily: e.target.value,
                        })
                      }
                      className="w-full rounded border border-gray-300 bg-white px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                      aria-label="字型"
                    >
                      {FONT_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="min-w-0 flex-1">
                    <label className="mb-0.5 block text-xs text-gray-500 dark:text-gray-400">
                      顏色
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={block.color}
                        onChange={e =>
                          updateWatermarkItem(block.id, {
                            color: e.target.value,
                          })
                        }
                        className="h-9 w-14 cursor-pointer rounded border border-gray-300 bg-white p-0 dark:border-gray-600 dark:bg-gray-700"
                        aria-label="顏色"
                      />
                      <input
                        type="text"
                        value={block.color}
                        onChange={e =>
                          updateWatermarkItem(block.id, {
                            color: e.target.value,
                          })
                        }
                        className="w-24 rounded border border-gray-300 bg-white px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                        placeholder="#000000"
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <div className="mb-1 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                    <span>大小 (px)</span>
                    <span>{block.fontSize}</span>
                  </div>
                  <input
                    type="range"
                    min={WATERMARK_FONT_SIZE_MIN}
                    max={fontMax}
                    step={2}
                    value={Math.max(
                      WATERMARK_FONT_SIZE_MIN,
                      Math.min(fontMax, block.fontSize)
                    )}
                    onChange={e =>
                      updateWatermarkItem(block.id, {
                        fontSize: Number(e.target.value),
                      })
                    }
                    className="w-full accent-primary"
                    aria-label="文字大小"
                  />
                </div>
                <div>
                  <div className="mb-1 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                    <span>不透明度</span>
                    <span>{Math.round(block.opacity * 100)}%</span>
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    step={5}
                    value={Math.round(block.opacity * 100)}
                    onChange={e =>
                      updateWatermarkItem(block.id, {
                        opacity: Number(e.target.value) / 100,
                      })
                    }
                    className="w-full accent-primary"
                  />
                </div>
              </div>
            )}
          </div>
        );
      })}

      <button
        type="button"
        onClick={addWatermarkItem}
        className="w-full rounded border border-dashed border-gray-300 py-2 text-sm text-gray-600 hover:border-primary hover:text-primary dark:border-gray-600 dark:text-gray-400 dark:hover:border-primary dark:hover:text-primary"
      >
        新增文字塊
      </button>

      <p className="text-xs text-gray-500 dark:text-gray-400">
        按下「輸出並下載」時會將文字繪製於最終畫布上（一律置中、多塊垂直堆疊）；留空則不套用。
      </p>
    </div>
  );
}
